import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ReplaySubject, Observable, of } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { State, toODataString } from '@progress/kendo-data-query';
import { DialogService, DialogRef, DialogCloseResult } from '@progress/kendo-angular-dialog';

import { ProductComponent } from './product-component.model';
import { UtilService } from 'src/app/shared/util.service';
import { LocalStorage } from 'src/app/core/local-storage.enum';
import { ProductComponentPopupComponent } from './product-component-popup/product-component-popup.component';

const apiUrl = environment.apiUrl;

@Injectable({
  providedIn: 'root',
})
export class ProductComponentService {
  private productComponents: ReplaySubject<ProductComponent[]> = new ReplaySubject<ProductComponent[]>(1);
  public productComponent: ReplaySubject<ProductComponent> = new ReplaySubject<ProductComponent>(1);
  public productComponentsOdata: ReplaySubject<any> = new ReplaySubject<any>(1);
  private dialog!: DialogRef;

  constructor(
    private http: HttpClient,
    private dialogService: DialogService,
    private utilService: UtilService
  ) {}

  list(): Observable<ProductComponent[]> {
    let uri = `${apiUrl}Product/ProductComponent`
    return this.http.get<ProductComponent[]>(uri).pipe(
      mergeMap((response: any) => {
        let value = response.value
        this.productComponents.next(value);
        return of(value)
      })
    );
  }

  read(id: number): Observable<ProductComponent> {
    return this.http
      .get<ProductComponent>(apiUrl + 'Product/ProductComponent/' + id, {})
      .pipe(
        tap((response: any) => {
          this.productComponent.next(response);
        })
      );
  }

  create(productComponent: any): Observable<any> {
    return this.http.post<any>(apiUrl + 'Product/ProductComponent/', productComponent).pipe(
      tap((response: any) => {
        //this.customer.next(response); // TODO: réactiver quand l'API va me retourner le record comme dans un get
      })
    );
  }

  update(productComponent: any): Observable<any> {
    // todo: potentiel manipulation a faire avec les champs date, checkbox, etc
    return this.http
      .put<any>(apiUrl + 'Product/ProductComponent/' + productComponent.productComponentId, productComponent)
      .pipe(
        tap((response: any) => {
          // this.customer.next(response); // TODO: réactiver quand l'API va me retourner le record comme dans un get
        })
      );
  }

  delete(productComponentId: any): Observable<any> {
    let uri = `${apiUrl}Product/ProductComponent/${productComponentId}`
    return this.http.delete<any>(uri);
  }

  changeOrder(productComponent: any): Observable<any> {
    return this.http
      .put<any>(apiUrl + 'Product/ProductComponent/ChangeOrder/' + productComponent.productComponentId, productComponent)
      .pipe(
        tap((response: any) => {
          // this.customer.next(response); // TODO: réactiver quand l'API va me retourner le record comme dans un get
        })
      );  
  }

  // ODATA : STATE

  odata(state: State): Observable<any> {

    let _state = this.utilService.makeStateCaseInsensitive(state)
    let uri = `${apiUrl}Product/ProductComponent?${toODataString(_state)}&$count=true`

    return this.http.get<ProductComponent>(uri).pipe(
      tap((response: any) => {
        this.productComponentsOdata.next(response);
      })
    );
  }

  // ODATA  : STRING

  odataStr(state: State, oDataString: string): Observable<any> {
    let oDataFullString = this.utilService.getODataFullString(state, oDataString);
    let uri = `${apiUrl}Product/ProductComponent?${oDataFullString}&$count=true`
    return this.http.get<any>(uri);
  }

  // POPUP LIST

  public popup(productId: number, customerId: number, userGroupHeaderId: number): any {
    const confirmObservable = new Observable((observer) => {
      // open dialog
      this.dialog = this.dialogService.open({
        content: ProductComponentPopupComponent,
        minWidth: 400,
        height: 800,
        maxWidth: 1200,
      });
      // set default values
      const dialogData = this.dialog.content.instance as ProductComponentPopupComponent;
      dialogData.productId = productId;
      dialogData.customerId = customerId;
      dialogData.userGroupHeaderId = userGroupHeaderId;
      
      // subscribe to response
      this.dialog.result.subscribe((result) => {
        if (result instanceof DialogCloseResult) {
          // cancel
          observer.next(false);
        } else {
          // confirm
          observer.next(result);
        }
      });
    });
    return confirmObservable;
  }

  
  /*
  * Cette fonction permet de filtrer les product components pour le produit du cart item
  * Si on trouve skipCustomerProductComponent, on skip TOUS les product components
  * Sinon, si des product components existent au niveau UserGroup on les prend et on exclu ceux au niveau client
  * Sinon on prend ceux au niveau client
  * 
  */
  public getEffectiveProductComponents(productId: number, userGroupHeaderId: number, itemProductComponentList: ProductComponent[]): ProductComponent[] {
    // on trouve d'abord les product components qui concerne le produit courant
    if (itemProductComponentList == null || itemProductComponentList.length == 0)
      return [];
    var filteredProductComponents = itemProductComponentList.filter((pc: ProductComponent) => {
      return pc.productId === productId
    });

    // on recherche les product components au niveau user group
    var userGroupProductComponents: ProductComponent[] = [];
    var userGroupHeaderId = userGroupHeaderId;
    if (userGroupHeaderId != null && userGroupHeaderId > 0) {
      userGroupProductComponents = filteredProductComponents.filter((pc: ProductComponent) => {
        return pc.userGroupHeaderId === userGroupHeaderId
      });
    }

    // si on trouve des product component au niveau du user group on prend uniquement ceux la,  sinon on prend ceux au niveau client
    if (userGroupProductComponents.length > 0) {
      filteredProductComponents = userGroupProductComponents;
    }
    else{
      filteredProductComponents = filteredProductComponents.filter((pc: ProductComponent) => {
        return pc.userGroupHeaderId == null || pc.userGroupHeaderId == 0
      });
    }

    // on vérifie si on doit skipper les product components créé sur le client
    var skipClientComponents = filteredProductComponents.some((pc: ProductComponent) => {
      return pc.childProductComponentType === 'SkipCustomerProductComponent';
    });

    if (skipClientComponents)
      return [];
    else
      return filteredProductComponents;
  }

}
